/**
 * LearnerFormEditor — the per-tenant learner record form (Phase SC-22).
 *
 * One list of standard + custom fields in display order. Super-admin
 * always edits; a tenant admin edits only when the platform allows it,
 * otherwise the same list renders read-only. Saving is the parent's
 * job: this component emits a full-replacement `LearnerFormUpdateRequest`.
 */
import * as React from "react";
import { ArrowDown, ArrowUp, Lock, Plus, Trash2 } from "lucide-react";
import type {
  LearnerFormCustomField,
  LearnerFormCustomFieldType,
  LearnerFormResponse,
  LearnerFormStandardFieldKind,
  LearnerFormUpdateRequest,
} from "@eduviaos/shared/types";
import { Badge } from "../components/badge.js";
import { Button } from "../components/button.js";
import { Input } from "../components/input.js";
import { Label } from "../components/label.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/select.js";
import { Switch } from "../components/switch.js";

const KIND_LABEL: Record<LearnerFormStandardFieldKind | LearnerFormCustomFieldType, string> = {
  text: "Text",
  email: "Email",
  phone: "Phone",
  date: "Date",
  select: "Choice list",
  textarea: "Long text",
  number: "Number",
};

const CUSTOM_TYPES: readonly LearnerFormCustomFieldType[] = [
  "text",
  "number",
  "date",
  "select",
  "phone",
  "email",
];

const LABEL_MAX = 60;
const OPTIONS_MAX = 50;

interface StandardRow {
  source: "standard";
  key: string;
  kind: LearnerFormStandardFieldKind;
  label: string;
  defaultLabel: string;
  visible: boolean;
  required: boolean;
  locked: boolean;
  helpText: string | null;
}

interface CustomRow {
  source: "custom";
  key: string;
  kind: LearnerFormCustomFieldType;
  label: string;
  visible: boolean;
  required: boolean;
  helpText: string | null;
  options: string[];
}

type Row = StandardRow | CustomRow;

function rowsFrom(form: LearnerFormResponse): Row[] {
  const standard = form.fields.map((f) => ({
    order: f.order,
    row: {
      source: "standard",
      key: f.key,
      kind: f.kind,
      label: f.label,
      defaultLabel: f.default_label,
      visible: f.visible,
      required: f.required,
      locked: f.locked,
      helpText: f.help_text,
    } satisfies StandardRow,
  }));
  const custom = form.custom_fields.map((f) => ({
    order: f.order,
    row: {
      source: "custom",
      key: f.key,
      kind: f.type,
      label: f.label,
      visible: f.visible,
      required: f.required,
      helpText: f.help_text,
      options: f.options,
    } satisfies CustomRow,
  }));
  return [...standard, ...custom].sort((a, b) => a.order - b.order).map((entry) => entry.row);
}

function toRequest(rows: Row[]): LearnerFormUpdateRequest {
  const body: LearnerFormUpdateRequest = { fields: [], custom_fields: [] };
  rows.forEach((row, index) => {
    const order = (index + 1) * 10;
    if (row.source === "standard") {
      const label = row.label.trim();
      body.fields.push({
        key: row.key,
        label: label && label !== row.defaultLabel ? label : null,
        visible: row.locked ? true : row.visible,
        required: row.locked ? true : row.visible && row.required,
        order,
        help_text: row.helpText,
      });
      return;
    }
    const field: LearnerFormCustomField = {
      key: row.key,
      label: row.label.trim(),
      type: row.kind,
      required: row.visible && row.required,
      visible: row.visible,
      order,
      help_text: row.helpText,
      options: row.kind === "select" ? row.options : [],
    };
    body.custom_fields.push(field);
  });
  return body;
}

/** `Seat hours` → `seat_hours`; unique against every key already in use. */
export function customFieldKeyFor(label: string, taken: ReadonlySet<string>): string {
  let base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 36);
  if (!/^[a-z]/.test(base)) base = `field_${base}`.replace(/_+$/g, "");
  if (base.length < 2) base = "field";
  let key = base;
  let n = 2;
  while (taken.has(key)) {
    key = `${base}_${n}`;
    n += 1;
  }
  return key;
}

function parseOptions(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(",")) {
    const value = part.trim();
    if (!value || seen.has(value.toLowerCase())) continue;
    seen.add(value.toLowerCase());
    out.push(value);
  }
  return out;
}

function rowError(row: Row): string | null {
  const label = row.label.trim();
  if (row.source === "custom" && !label) return "Give this field a name.";
  if (label.length > LABEL_MAX) return `Keep the name under ${LABEL_MAX} characters.`;
  if (row.source === "custom" && row.kind === "select") {
    if (row.options.length === 0) return "Add at least one choice.";
    if (row.options.length > OPTIONS_MAX) return `Use at most ${OPTIONS_MAX} choices.`;
  }
  return null;
}

export interface LearnerFormEditorProps {
  form: LearnerFormResponse;
  /** Renders the list without any controls. */
  readOnly?: boolean;
  saving?: boolean;
  onSave?: (body: LearnerFormUpdateRequest) => void;
  /** Singular noun for the person this form describes, e.g. "learner". */
  personNoun?: string;
  /**
   * Standard fields that mean nothing for this institution (a study
   * centre's grade). They stay out of the list and keep their stored
   * settings on save.
   */
  omitKeys?: readonly string[];
}

export function LearnerFormEditor({
  form,
  readOnly = false,
  saving = false,
  onSave,
  personNoun = "learner",
  omitKeys = [],
}: LearnerFormEditorProps) {
  const omitKey = omitKeys.join("|");
  const [initialRows, omittedRows] = React.useMemo(() => {
    const omit = new Set(omitKey ? omitKey.split("|") : []);
    const all = rowsFrom(form);
    return [all.filter((r) => !omit.has(r.key)), all.filter((r) => omit.has(r.key))];
  }, [form, omitKey]);
  const toBody = React.useCallback(
    (list: Row[]) => toRequest([...list, ...omittedRows]),
    [omittedRows],
  );
  const [rows, setRows] = React.useState<Row[]>(initialRows);
  const [newLabel, setNewLabel] = React.useState("");
  const [newType, setNewType] = React.useState<LearnerFormCustomFieldType>("text");
  const [newOptions, setNewOptions] = React.useState("");
  const [newRequired, setNewRequired] = React.useState(false);
  const [addError, setAddError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const dirty = React.useMemo(
    () => JSON.stringify(toBody(rows)) !== JSON.stringify(toBody(initialRows)),
    [rows, initialRows, toBody],
  );
  const errors = React.useMemo(() => new Map(rows.map((r) => [r.key, rowError(r)])), [rows]);
  const hasErrors = [...errors.values()].some(Boolean);
  const customCount = rows.filter((r) => r.source === "custom").length;
  const atCustomLimit = customCount >= form.max_custom_fields;

  const update = (key: string, patch: Partial<StandardRow> | Partial<CustomRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? ({ ...r, ...patch } as Row) : r)));
  };

  const move = (index: number, delta: -1 | 1) => {
    setRows((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });
  };

  const remove = (key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  };

  const addCustom = () => {
    const label = newLabel.trim();
    if (!label) {
      setAddError("Give the new field a name.");
      return;
    }
    if (label.length > LABEL_MAX) {
      setAddError(`Keep the name under ${LABEL_MAX} characters.`);
      return;
    }
    const options = newType === "select" ? parseOptions(newOptions) : [];
    if (newType === "select" && options.length === 0) {
      setAddError("Add the choices, separated by commas.");
      return;
    }
    if (options.length > OPTIONS_MAX) {
      setAddError(`Use at most ${OPTIONS_MAX} choices.`);
      return;
    }
    const taken = new Set([...rows, ...omittedRows].map((r) => r.key));
    const row: CustomRow = {
      source: "custom",
      key: customFieldKeyFor(label, taken),
      kind: newType,
      label,
      visible: true,
      required: newRequired,
      helpText: null,
      options,
    };
    setRows((prev) => [...prev, row]);
    setNewLabel("");
    setNewOptions("");
    setNewRequired(false);
    setNewType("text");
    setAddError(null);
  };

  if (readOnly) {
    return (
      <ol className="divide-border/60 divide-y rounded-lg border" data-testid="learner-form-list">
        {rows
          .filter((row) => row.visible)
          .map((row) => (
            <li key={row.key} className="flex items-center gap-3 px-4 py-3 text-sm">
              <span className="min-w-0 flex-1 truncate font-medium">{row.label}</span>
              <span className="text-muted-foreground text-xs">{KIND_LABEL[row.kind]}</span>
              <Badge variant="outline">{row.required ? "Required" : "Optional"}</Badge>
            </li>
          ))}
      </ol>
    );
  }

  return (
    <div className="grid gap-6">
      <ol className="divide-border/60 divide-y rounded-lg border" data-testid="learner-form-list">
        {rows.map((row, index) => {
          const error = errors.get(row.key);
          const inputId = `learner-form-label-${row.key}`;
          const errorId = `${inputId}-error`;
          const name = row.label.trim() || "this field";
          return (
            <li key={row.key} className="grid gap-2 px-3 py-3 sm:px-4">
              <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:gap-3">
                <div className="flex shrink-0 flex-col">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label={`Move ${name} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp className="size-3.5" aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label={`Move ${name} down`}
                    disabled={index === rows.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown className="size-3.5" aria-hidden="true" />
                  </Button>
                </div>
                <div className="grid min-w-0 flex-1 gap-1">
                  <Label htmlFor={inputId} className="sr-only">
                    Name shown for {row.source === "standard" ? row.defaultLabel : name}
                  </Label>
                  <Input
                    id={inputId}
                    value={row.label}
                    maxLength={LABEL_MAX + 1}
                    placeholder={row.source === "standard" ? row.defaultLabel : "Field name"}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                    onChange={(e) => update(row.key, { label: e.target.value })}
                  />
                  <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    {KIND_LABEL[row.kind]}
                    {row.source === "custom" ? (
                      <Badge variant="secondary" className="text-[10px]">
                        Extra field
                      </Badge>
                    ) : null}
                    {row.source === "custom" && row.kind === "select"
                      ? ` · ${row.options.join(", ")}`
                      : null}
                  </p>
                </div>
                {row.source === "standard" && row.locked ? (
                  <p className="text-muted-foreground flex shrink-0 items-center gap-1.5 text-xs">
                    <Lock className="size-3.5" aria-hidden="true" />
                    Always shown and required
                  </p>
                ) : (
                  <div className="flex shrink-0 items-center gap-4">
                    <label className="flex items-center gap-2 text-xs">
                      <Switch
                        checked={row.visible}
                        aria-label={`Show ${name}`}
                        onCheckedChange={(checked) => update(row.key, { visible: checked })}
                      />
                      Shown
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <Switch
                        checked={row.visible && row.required}
                        disabled={!row.visible}
                        aria-label={`Require ${name}`}
                        onCheckedChange={(checked) => update(row.key, { required: checked })}
                      />
                      Required
                    </label>
                  </div>
                )}
                {row.source === "custom" ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive size-8 shrink-0"
                    aria-label={`Remove ${name}`}
                    onClick={() => remove(row.key)}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
              {error ? (
                <p id={errorId} className="text-destructive text-xs" role="alert">
                  {error}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>

      <section
        aria-labelledby="learner-form-add-heading"
        className="bg-muted/30 grid gap-3 rounded-lg border p-4"
      >
        <div>
          <h3 id="learner-form-add-heading" className="text-sm font-semibold">
            Add an extra field
          </h3>
          <p className="text-muted-foreground text-xs">
            Extra details you want on every {personNoun}&apos;s record. {customCount} of{" "}
            {form.max_custom_fields} used.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="grid gap-1.5">
            <Label htmlFor="learner-form-new-label">Field name</Label>
            <Input
              id="learner-form-new-label"
              value={newLabel}
              maxLength={LABEL_MAX + 1}
              placeholder="e.g. Seat hours per day"
              disabled={atCustomLimit}
              onChange={(e) => setNewLabel(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="learner-form-new-type">Type</Label>
            <Select
              value={newType}
              onValueChange={(v) => setNewType(v as LearnerFormCustomFieldType)}
              disabled={atCustomLimit}
            >
              <SelectTrigger id="learner-form-new-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CUSTOM_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {KIND_LABEL[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {newType === "select" ? (
          <div className="grid gap-1.5">
            <Label htmlFor="learner-form-new-options">Choices</Label>
            <Input
              id="learner-form-new-options"
              value={newOptions}
              placeholder="Morning, Afternoon, Evening"
              onChange={(e) => setNewOptions(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">Separate choices with commas.</p>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={newRequired}
              onCheckedChange={setNewRequired}
              disabled={atCustomLimit}
              aria-label="Require the new field"
            />
            Required
          </label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCustom}
            disabled={atCustomLimit}
          >
            <Plus className="size-4" aria-hidden="true" />
            Add field
          </Button>
        </div>
        {addError ? (
          <p className="text-destructive text-xs" role="alert">
            {addError}
          </p>
        ) : null}
        {atCustomLimit ? (
          <p className="text-muted-foreground text-xs">
            You&apos;ve reached the limit of {form.max_custom_fields} extra fields.
          </p>
        ) : null}
      </section>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={!dirty || saving}
          onClick={() => setRows(initialRows)}
        >
          Discard changes
        </Button>
        <Button
          type="button"
          disabled={!dirty || hasErrors || saving || !onSave}
          onClick={() => onSave?.(toBody(rows))}
        >
          {saving ? "Saving…" : "Save form"}
        </Button>
      </div>
    </div>
  );
}
